import { Helmet } from 'react-helmet-async';
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Search, MapPin, Globe, Briefcase, Filter, Sparkles, Lock, Info } from 'lucide-react';
import OfferCard from '../components/OfferCard';
import OfferDetailView from '../components/OfferDetailView';
import MobileOfferDrawer from '../components/MobileOfferDrawer';
import ModernMultiSelect from '../components/ModernMultiSelect';
import ModernPagination from '../components/ModernPagination';
import { supabase } from '../supabaseClient';
import { track } from '../lib/analytics';
import PremiumModal from '../components/PremiumModal';
import { useEnsureEmbedding } from '../hooks/useEnsureEmbedding';

const ITEMS_PER_PAGE = 20;

const OffersPlusPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    // Core State
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedOffer, setSelectedOffer] = useState(null);
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
    const [favorites, setFavorites] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('favorite_offers') || '[]');
        } catch {
            return [];
        }
    });

    // Viewed offers tracking
    const [viewedOffers, setViewedOffers] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('viewed_offers') || '[]');
        } catch {
            return [];
        }
    });

    // Premium State
    const [isPremium, setIsPremium] = useState(false);
    const [checkingPremium, setCheckingPremium] = useState(true);
    const [isCvMatchActive, setIsCvMatchActive] = useState(false);
    const { ensureEmbedding, isUpdating: isEmbeddingUpdating } = useEnsureEmbedding();

    // Filter State (Simplified for now, similar to OffersPage)
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCountry, setSelectedCountry] = useState([]);
    const [selectedSector, setSelectedSector] = useState([]);
    const [selectedDuration, setSelectedDuration] = useState([]);
    const [sectors, setSectors] = useState([]);
    const [durations, setDurations] = useState([]);
    const [showCvMatchingInfo, setShowCvMatchingInfo] = useState(false);
    const [showPremiumModal, setShowPremiumModal] = useState(false);

    // Pagination
    const [page, setPage] = useState(0);
    const [totalCount, setTotalCount] = useState(0);

    // Initial Premium Check & Fetch
    useEffect(() => {
        const fetchFilters = async () => {
            // Fetch Sectors for filter dropdown
            const { data: sectorData } = await supabase
                .from('offres_vie_hors_bf_public')
                .select('parent_label_fr')
                .eq('url_live', 'active');

            if (sectorData) {
                const unique = [...new Set(sectorData.map(d => d.parent_label_fr).filter(s => s))].sort();
                setSectors(unique);
            }

            // Fetch Durations for filter dropdown
            const { data: durationData } = await supabase
                .from('offres_vie_hors_bf_public')
                .select('duree')
                .eq('url_live', 'active')
                .not('duree', 'is', null);

            if (durationData) {
                const unique = [...new Set(durationData.map(d => d.duree).filter(d => d && d.toString().trim()))]
                    .sort((a, b) => {
                        const numA = parseInt(a);
                        const numB = parseInt(b);
                        if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
                        return String(a).localeCompare(String(b));
                    });
                setDurations(unique);
            }
        };
        fetchFilters();
    }, []);

    useEffect(() => {
        const checkPremiumAndFetch = async () => {
            setLoading(true);
            try {
                const { data: { session } } = await supabase.auth.getSession();
                let premiumStatus = false;

                if (session) {
                    const { data: profile } = await supabase
                        .from('profiles')
                        .select('is_premium')
                        .eq('id', session.user.id)
                        .single();
                    premiumStatus = profile?.is_premium || false;
                }
                setIsPremium(premiumStatus);
                setCheckingPremium(false);

                // 1. Fetch Global Count (for display "X offers available")
                // We always want to show the full potential count from the main table so users see what they are missing
                const { count: realTotalCount } = await supabase
                    .from('offres_vie_hors_bf_public')
                    .select('id_offre', { count: 'exact', head: true })
                    .eq('url_live', 'active');

                if (realTotalCount) setTotalCount(realTotalCount);

                let fetchedOffers = [];
                let count = 0;

                // 2. Fetch Offers Logic
                if (premiumStatus) {
                    // === PREMIUM USER ===

                    if (isCvMatchActive) {
                        // --- CV MATCHING ---
                        const { data: { user } } = await supabase.auth.getUser();
                        if (user) {
                            const { data: profile } = await supabase
                                .from('profiles')
                                .select('embedding, updated_at, last_cv_update')
                                .eq('id', user.id)
                                .single();

                            const userEmbedding = profile?.embedding;

                            // [Refactored] Use unified hook instead of manual check
                            // NOTE: Since this is inside useEffect, we can't easily use the hook's returned function nicely here 
                            // because `checkPremiumAndFetch` is called on mount.
                            // BUT: The user manually toggles `isCvMatchActive`.
                            // So we should do the check WHEN they toggle, not inside the fetch loop.
                            // 
                            // However, in this file, we don't have a separate toggle function, it's inline in the JSX onClick.
                            // We need to refactor the toggle logic first.

                            // For now, let's assuming if `isCvMatchActive` is true, simple validation or just trust the toggle logic?
                            // Actually, in fetch loop we just need the embedding.

                            // If active but no embedding (weird state), disable
                            if (!userEmbedding) {
                                setIsCvMatchActive(false);
                            }

                            if (userEmbedding) {
                                console.log("🔍 Calling CV Matching with embedding len:", userEmbedding.length);
                                const { data: rpcData, error: rpcError } = await supabase.rpc('cv_matching_exclusive', {
                                    embedding: userEmbedding,
                                    match_threshold: 0.1, // Lowered from 0.5 to 0.1 for debugging
                                    match_count: 50
                                });

                                if (rpcError) {
                                    console.error("CV Match Error:", rpcError);
                                } else {
                                    console.log("✅ CV Matching Results:", rpcData?.length || 0, "offers found.");
                                    track('feature_used', { feature: 'cv_match_exclusive', results_count: rpcData?.length || 0 });
                                    fetchedOffers = rpcData || [];
                                    count = fetchedOffers.length;
                                }
                            } else {
                                // [NEW] Notify user if no embedding found
                                alert("Aucun CV analysé trouvé. Veuillez uploader votre CV dans votre profil pour utiliser le matching.");
                                setIsCvMatchActive(false);
                            }
                        }
                    } else {
                        // --- STANDARD FILTERING ---
                        let query = supabase
                            .from('offres_vie_hors_bf_public')
                            .select('*', { count: 'exact' })
                            .eq('url_live', 'active');

                        if (selectedCountry.length > 0) query = query.in('pays', selectedCountry);
                        if (selectedSector.length > 0) query = query.in('parent_label_fr', selectedSector);
                        if (selectedDuration.length > 0) query = query.in('duree', selectedDuration);
                        if (searchQuery) {
                            query = query.or(`intitule_poste.ilike.%${searchQuery}%,entreprise.ilike.%${searchQuery}%,pays.ilike.%${searchQuery}%,ville.ilike.%${searchQuery}%`);
                        }

                        // Pagination
                        query = query.range(page * ITEMS_PER_PAGE, (page + 1) * ITEMS_PER_PAGE - 1);

                        const res = await query;
                        if (res.error) {
                            console.error("Supabase Fetch Error:", res.error);
                            throw res.error;
                        }
                        fetchedOffers = res.data || [];
                        count = res.count || fetchedOffers.length;
                    }

                } else {
                    // === NON-PREMIUM USER ===
                    // Fetch from the FREE table. Sort is handled by DB.
                    let queryFree = supabase
                        .from('offres_vie_hors_bf_public_free')
                        .select('*')
                        .eq('url_live', 'active');

                    if (selectedCountry.length > 0) queryFree = queryFree.in('pays', selectedCountry);
                    if (selectedSector.length > 0) queryFree = queryFree.in('parent_label_fr', selectedSector);
                    if (selectedDuration.length > 0) queryFree = queryFree.in('duree', selectedDuration);
                    if (searchQuery) {
                        queryFree = queryFree.or(`intitule_poste.ilike.%${searchQuery}%,entreprise.ilike.%${searchQuery}%,pays.ilike.%${searchQuery}%,ville.ilike.%${searchQuery}%`);
                    }

                    const { data: freeOffers, error: freeError } = await queryFree.limit(3);

                    if (freeError) {
                        console.error("Free Offers Fetch Error:", freeError);
                    }
                    fetchedOffers = freeOffers || [];

                    // We don't overwrite totalCount here, we want to keep the 'realTotalCount' 
                    // from the main table to show the user how many offers exist.
                }

                // Helper for country codes
                const getCountryCode = (countryName, code) => {
                    if (code) return code;
                    if (!countryName) return 'FR';

                    const normalizedName = countryName.toUpperCase()
                        .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
                        .trim();

                    const map = {
                        'ETATS-UNIS': 'US', 'ETATS UNIS': 'US', 'USA': 'US',
                        'ROYAUME-UNI': 'GB', 'ROYAUME UNI': 'GB', 'ANGLETERRE': 'GB',
                        'ALLEMAGNE': 'DE', 'ESPAGNE': 'ES', 'ITALIE': 'IT',
                        'BELGIQUE': 'BE', 'SUISSE': 'CH', 'CANADA': 'CA',
                        'AUSTRALIE': 'AU', 'JAPON': 'JP', 'CHINE': 'CN',
                        'BRESIL': 'BR', 'INDE': 'IN', 'MEXIQUE': 'MX',
                        'PAYS-BAS': 'NL', 'SUEDE': 'SE', 'NORVEGE': 'NO',
                        'DANEMARK': 'DK', 'FINLANDE': 'FI', 'IRELANDE': 'IE',
                        'PORTUGAL': 'PT', 'AUTRICHE': 'AT', 'POLOGNE': 'PL',
                        'REPUBLIQUE TCHEQUE': 'CZ', 'HONGRIE': 'HU', 'GRECE': 'GR',
                        'TURQUIE': 'TR', 'EMIRATS ARABES UNIS': 'AE', 'DUBAI': 'AE',
                        'SINGAPOUR': 'SG', 'HONG KONG': 'HK', 'COREE DU SUD': 'KR',
                        'VIETNAM': 'VN', 'THAILANDE': 'TH', 'INDONESIE': 'ID',
                        'MALAISIE': 'MY', 'PHILIPPINES': 'PH', 'CAMBODGE': 'KH',
                        'LAOS': 'LA', 'EGYPTE': 'EG', 'AFRIQUE DU SUD': 'ZA', 'COLOMBIE': 'CO',
                        'PEROU': 'PE', 'CHILI': 'CL', 'ARGENTINE': 'AR', 'TAIWAN': 'TW',
                        'ARABIE SAOUDITE': 'SA', 'QATAR': 'QA', 'KOWEIT': 'KW', 'LIBAN': 'LB',
                        'JORDANIE': 'JO', 'MAURICE': 'MU', 'KENYA': 'KE', 'MADAGASCAR': 'MG',
                        'CONGO': 'CG', 'REPUBLIQUE DU CONGO': 'CG',
                        'REPUBLIQUE DEMOCRATIQUE DU CONGO': 'CD', 'RDC': 'CD',
                        'ANGOLA': 'AO', 'NIGERIA': 'NG', 'TUNISIE': 'TN', 'AZERBAIDJAN': 'AZ'
                    };
                    return map[normalizedName] || 'FR';
                };

                // Map Real Offers
                let mappedOffers = fetchedOffers.map(off => ({
                    id: off.id_offre || off.id,
                    title: off.intitule_poste || 'Poste sans titre',
                    company: off.entreprise,
                    countryCode: getCountryCode(off.pays, off.country_code),
                    country: off.pays || 'France',
                    city: off.ville || 'TBD',
                    sector: off.parent_label_fr || 'Général',
                    badge: 'Exclusif',
                    duree: off.duree,
                    duration: off.duree ? `${off.duree} mois` : '12 mois',
                    date: off.date_publication ? new Date(off.date_publication).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) : 'Récemment',
                    summary: off.sidebar_summary || (off.offre_complete || '').substring(0, 160) + '...',
                    description: off.offre_complete,
                    link: off.lien_offre || '#',
                    companyHtml: off.company_html,
                    missionHtml: off.mission_html,
                    profileHtml: off.profile_html,
                    isExclusive: true
                }));

                const urlId = searchParams.get('id');
                if (urlId) {
                    if (!premiumStatus) {
                        // Free user trying to access specific premium offer -> block & show modal
                        setShowPremiumModal(true);
                        // Clean up the URL to avoid looping the modal
                        navigate('/offres-plus', { replace: true });
                    } else {
                        // Premium user -> check if in current loaded offers
                        const foundInCache = mappedOffers.find(o => o.id === urlId);
                        if (foundInCache) {
                            setSelectedOffer(foundInCache);
                        } else {
                            // Fetch specific offer from DB
                            const { data: specificReq, error: specificErr } = await supabase
                                .from('offres_vie_hors_bf_public')
                                .select('*')
                                .eq('id_offre', urlId)
                                .single();
                                
                            if (!specificErr && specificReq) {
                                const specificOffer = {
                                    id: specificReq.id_offre || specificReq.id,
                                    title: specificReq.intitule_poste || 'Poste sans titre',
                                    company: specificReq.entreprise,
                                    countryCode: getCountryCode(specificReq.pays, specificReq.country_code),
                                    country: specificReq.pays || 'France',
                                    city: specificReq.ville || 'TBD',
                                    sector: specificReq.parent_label_fr || 'Général',
                                    badge: 'Exclusif',
                                    duree: specificReq.duree,
                                    duration: specificReq.duree ? `${specificReq.duree} mois` : '12 mois',
                                    date: specificReq.date_publication ? new Date(specificReq.date_publication).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) : 'Récemment',
                                    summary: specificReq.sidebar_summary || (specificReq.offre_complete || '').substring(0, 160) + '...',
                                    description: specificReq.offre_complete,
                                    link: specificReq.lien_offre || '#',
                                    companyHtml: specificReq.company_html,
                                    missionHtml: specificReq.mission_html,
                                    profileHtml: specificReq.profile_html,
                                    isExclusive: true
                                };
                                // Prepend it to mappedOffers so it's visible in the list
                                mappedOffers = [specificOffer, ...mappedOffers];
                                setSelectedOffer(specificOffer);
                            }
                        }
                    }
                }

                // If Non-Premium, Generate Ghost Offers (No client-side sorting needed)
                if (!premiumStatus) {
                    // 1. Get Real Count from Hidden Table (RPC)
                    const { data: trueTotalCount } = await supabase.rpc('get_active_offers_count', {
                        p_country: selectedCountry.length > 0 ? selectedCountry[0] : '', // RPC only supports single string for now
                        p_sector: selectedSector.length > 0 ? selectedSector[0] : '',
                        p_search: searchQuery || ''
                    });

                    const realHiddenCount = trueTotalCount || 0;
                    const visibleCount = mappedOffers.length;

                    // 2. Determine Ghost Count
                    // If real hidden count > visible count, show difference up to 12
                    const neededGhosts = Math.max(0, realHiddenCount - visibleCount);
                    const ghostCount = Math.min(12, neededGhosts);

                    // GENERATE RANDOM GHOST OFFERS with Valid Flags
                    const ghostTitles = [
                        "Business Analyst", "Contrôleur de Gestion", "Ingénieur Qualité / HSE",
                        "Chef de Projet Marketing", "Junior Buyer / Acheteur", "Supply Chain Coordinator",
                        "Responsable Communication", "Ingénieur R&D", "Data Analyst", "Brand Manager Junior",
                        "Assistant Export", "Chargé d'Affaires International"
                    ];
                    const ghostSectors = [
                        "Luxe & Mode", "Finance & Banque", "Industrie Automobile",
                        "Aéronautique", "Grande Distribution", "Santé & Pharma"
                    ];
                    // Ensure codes match regex / list of available assets
                    const ghostCountries = [
                        { name: "États-Unis", code: "US" },
                        { name: "Royaume-Uni", code: "GB" },
                        { name: "Allemagne", code: "DE" },
                        { name: "Singapour", code: "SG" },
                        { name: "Canada", code: "CA" },
                        { name: "Japon", code: "JP" },
                        { name: "Australie", code: "AU" },
                        { name: "Espagne", code: "ES" },
                        { name: "Italie", code: "IT" },
                        { name: "Suisse", code: "CH" },
                        { name: "Chine", code: "CN" },
                        { name: "Brésil", code: "BR" }
                    ];

                    const ghostOffers = Array.from({ length: ghostCount }).map((_, i) => {
                        const rTitle = ghostTitles[Math.floor(Math.random() * ghostTitles.length)];
                        const rSector = ghostSectors[Math.floor(Math.random() * ghostSectors.length)];
                        const rCountry = ghostCountries[Math.floor(Math.random() * ghostCountries.length)];

                        return {
                            id: `ghost-${i}`,
                            title: rTitle,
                            company: 'Grand Groupe Confidentiel',
                            countryCode: rCountry.code,
                            country: rCountry.name,
                            city: 'Ville masquée',
                            sector: rSector,
                            badge: 'Exclusif',
                            duree: null,
                            duration: '12-24 mois',
                            date: 'Récemment',
                            summary: 'Le contenu de cette offre est masqué pour protéger l\'exclusivité de nos partenaires. Rejoignez Trouve+ pour accéder au détail complet et postuler.',
                            description: '',
                            link: '#',
                            isExclusive: true,
                            isGhost: true,
                            trackingMode: false
                        };
                    });

                    setOffers([...mappedOffers, ...ghostOffers]);

                    // KEY FIX: Show the TRUE count from the hidden table
                    setTotalCount(realHiddenCount);
                } else {
                    setOffers(mappedOffers);
                    setTotalCount(count);
                }

                if (mappedOffers.length > 0) setSelectedOffer(mappedOffers[0]);

            } catch (err) {
                console.error("Error in OffersPlusPage:", err);
            } finally {
                setLoading(false);
            }
        };

        checkPremiumAndFetch();
    }, [isPremium, page, selectedCountry, selectedSector, selectedDuration, searchQuery, isCvMatchActive]); // Re-run if filters change (only relevant for premium really)


    const toggleFavorite = (offerId) => {
        if (!favorites.includes(offerId)) track('offer_favorited', { offer_id: offerId, source: 'hors_bf' });
        setFavorites(prev => {
            const newFavs = prev.includes(offerId) ? prev.filter(id => id !== offerId) : [...prev, offerId];
            localStorage.setItem('favorite_offers', JSON.stringify(newFavs));
            return newFavs;
        });
    };

    const handleOfferClick = (offer) => {
        // Only allow clicking on non-blurred items or if premium
        const index = offers.findIndex(o => o.id === offer.id);
        if (!isPremium && index >= 3) return; // Prevent clicking blurred items

        track('offer_viewed', { offer_id: offer.id, source: 'hors_bf', company: offer.company, country: offer.country });
        setSelectedOffer(offer);
        if (window.innerWidth < 768) setMobileDrawerOpen(true);
    };

    return (
        <div className="min-h-screen bg-[#F0F9FF] py-6 lg:py-10 px-4 sm:px-6 lg:px-8">
            <Helmet><meta name='robots' content='noindex, nofollow' /></Helmet>
            <div className="max-w-7xl mx-auto">

                {/* ========== PREMIUM HEADER BANNER (Companies Style) ========== */}
                <div className="w-full pb-4">
                    <div className="bg-violet-400/15 border border-violet-100/30 rounded-xl p-6 flex items-center gap-8 shadow-sm relative overflow-hidden">
                        {/* Tilted Mascot Frame */}
                        <div className="hidden md:block flex-shrink-0 w-32 h-32 relative z-10 transform rotate-3">
                            <div className="w-full h-full rounded-2xl overflow-hidden border-4 border-white/50 shadow-md bg-white">
                                <img
                                    src="/assets/mascots/woman-3.png"
                                    alt="Mascot"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </div>

                        <div className="flex-grow z-10 flex flex-col py-1 h-full justify-center">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="flex items-center justify-center px-4 py-2 bg-slate-900 rounded-full">
                                    <img
                                        src="/assets/Trouve+white.png"
                                        alt="Trouve+"
                                        className="h-7 w-auto object-contain"
                                    />
                                </div>
                                <span className="text-sm font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-violet-600 uppercase tracking-wide">
                                    PREMIUM
                                </span>
                            </div>

                            <h1 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight leading-none mb-2">
                                Prenez une longueur d'avance avec <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-violet-600">Trouve+</span>
                            </h1>

                            <div className="text-slate-600 text-sm font-medium leading-relaxed max-w-4xl space-y-4">
                                <p>
                                    Ne vous contentez plus des restes. La majorité des grands groupes publient leurs offres V.I.E. sur leurs propres sites carrières <strong>plusieurs jours avant</strong> Business France. Parfois, elles n'y sont même jamais diffusées.
                                </p>
                                <p>
                                    Avec <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-violet-600">Trouve+</span>, nous centralisons en temps réel les opportunités de 50 recruteurs majeurs.
                                </p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li><strong>Exclusivité :</strong> Accédez au "marché caché" des offres non publiées sur Business France.</li>
                                    <li><strong>Priorité :</strong> Soyez le premier à postuler dès la mise en ligne pour maximiser vos chances d'entretien.</li>
                                    <li><strong>Double Visibilité :</strong> Multipliez vos points de contact en postulant sur les deux plateformes simultanément.</li>
                                </ul>
                            </div>

                            {!isPremium && (
                                <div className="mt-4">
                                    <button
                                        onClick={() => navigate('/trouve-plus')}
                                        className="inline-flex items-center justify-center px-5 py-2 bg-slate-900 text-white rounded-lg text-sm font-bold hover:bg-slate-800 transition-colors shadow-lg shadow-slate-900/10"
                                    >
                                        Découvrir l'offre
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex flex-col lg:flex-row gap-6 mb-8 justify-between items-start lg:items-center">

                    {/* CV Matching Button */}
                    <div className="relative w-full lg:w-auto">
                        <div className="relative">
                            <div
                                className={`rounded-2xl py-2 px-4 border-2 transition-all cursor-pointer active:scale-[0.98] shadow-sm hover:shadow-md ${isCvMatchActive
                                    ? 'bg-violet-500/20 border-violet-400'
                                    : 'bg-violet-400/10 border-violet-300'}`}
                                onClick={async () => {
                                    if (isPremium) {
                                        if (!isCvMatchActive) {
                                            // Turning ON: Check/Update Embedding
                                            const embedding = await ensureEmbedding();
                                            if (embedding) setIsCvMatchActive(true);
                                        } else {
                                            setIsCvMatchActive(false);
                                        }
                                    } else {
                                        setShowPremiumModal(true);
                                    }
                                }}
                            >
                                <div className="flex items-center gap-3">
                                    {/* Premium Badge */}
                                    <div className="flex items-center justify-center px-2.5 py-1 bg-slate-900 rounded-full">
                                        <img
                                            src="/assets/Trouve+white.png"
                                            alt="Trouve+"
                                            className="h-4 w-auto object-contain"
                                        />
                                    </div>

                                    {/* CV Matching Text */}
                                    <span className="text-violet-600 font-bold text-sm">
                                        {isEmbeddingUpdating ? 'Mise à jour...' : (isCvMatchActive ? 'Matching Activé ✓' : 'CV Matching')}
                                    </span>

                                    {/* Info Icon */}
                                    <Info
                                        className="w-4 h-4 text-slate-400 hover:text-violet-600 transition-colors"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            alert("Active le CV Matching pour afficher en priorité les offres qui te correspondent.");
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Filters Group */}
                    <div className="flex flex-wrap gap-3 items-center flex-1">
                        <ModernMultiSelect
                            value={selectedCountry}
                            options={['France', 'Allemagne', 'Espagne', 'États-Unis', 'Canada', 'Royaume-Uni', 'Suisse', 'Belgique', 'Italie'].map(c => ({ value: c, label: c }))}
                            onChange={setSelectedCountry}
                            placeholder="Tous pays"
                            className="min-w-[160px] flex-1 lg:flex-none"
                        />
                        <ModernMultiSelect
                            value={selectedSector}
                            options={sectors.map(s => ({ value: s, label: s }))}
                            onChange={setSelectedSector}
                            placeholder="Toutes spécialisations"
                            className="min-w-[200px] flex-1 lg:flex-none"
                        />
                        {durations.length > 0 && (
                            <ModernMultiSelect
                                value={selectedDuration}
                                options={durations.map(d => ({ value: d, label: `${d} mois` }))}
                                onChange={setSelectedDuration}
                                placeholder="Toutes durées"
                                className="min-w-[160px] flex-1 lg:flex-none"
                            />
                        )}
                    </div>

                    {/* Search Bar */}
                    <div className="flex flex-1 justify-end w-full lg:w-auto">
                        <div className="relative w-full max-w-[400px]">
                            <input
                                type="text"
                                placeholder="Rechercher par pays, entreprise, intitulé..."
                                className="w-full pl-5 pr-10 py-3 rounded-full border border-gray-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-100 text-sm font-medium text-slate-600 placeholder:text-slate-400"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        </div>
                    </div>
                </div>

                {/* Total Count & Pagination */}
                <div className="flex flex-col sm:flex-row justify-between items-center mb-6 px-2 gap-4">
                    <div className="text-sm text-slate-500 font-medium">
                        {totalCount > 0 ? `${totalCount} offres exclusives disponibles` : 'Aucune offre trouvée'}
                    </div>

                    {/* Pagination (Top Right) */}
                    {offers.length > 0 && totalCount > ITEMS_PER_PAGE && (
                        <ModernPagination
                            currentPage={page}
                            totalPages={Math.ceil(totalCount / ITEMS_PER_PAGE)}
                            onPageChange={setPage}
                        />
                    )}
                </div>


                {/* ========== OFFERS GRID ========== */}
                <div className="grid grid-cols-1 lg:grid-cols-13 gap-8 items-start relative mt-4">

                    {/* Offers List */}
                    <div className="lg:col-span-5 flex flex-col gap-4">



                        {loading ? (
                            <div className="flex flex-col gap-4">
                                {[1, 2, 3].map(i => <div key={i} className="h-32 bg-white/50 rounded-2xl animate-pulse" />)}
                            </div>
                        ) : (
                            <>
                                {offers.length > 0 ? (
                                    <>
                                        {/* Visible Offers (Top 3 for non-premium, All for premium) */}
                                        {offers.slice(0, isPremium ? offers.length : 3).map((offer) => (
                                            <OfferCard
                                                key={offer.id}
                                                offer={offer}
                                                isSelected={selectedOffer?.id === offer.id}
                                                onClick={handleOfferClick}
                                                isFavorite={favorites.includes(offer.id)}
                                                onToggleFavorite={toggleFavorite}
                                                isViewed={viewedOffers.includes(offer.id)}
                                            />
                                        ))}

                                        {/* Blurred Offers Container (Non-Premium only) */}
                                        {!isPremium && offers.length > 3 && (
                                            <div className="relative mt-4">
                                                {/* Blurred List - Render items 3 to 15 */}
                                                <div className="flex flex-col gap-4 blur-sm opacity-60 select-none pointer-events-none">
                                                    {offers.slice(3, 15).map((offer, idx) => (
                                                        <OfferCard
                                                            key={`blurred-${offer.id || idx}`}
                                                            offer={offer}
                                                            isSelected={false}
                                                            onClick={(e) => e.preventDefault()}
                                                            isFavorite={false}
                                                            onToggleFavorite={() => { }}
                                                        />
                                                    ))}
                                                </div>

                                                {/* Sticky Overlay / Mask */}
                                                <div className="absolute inset-0 z-20 flex flex-col items-center pt-20">
                                                    <div className="w-full max-w-sm mx-auto p-8 rounded-3xl bg-white/90 backdrop-blur-md shadow-2xl border border-white/20 text-center">
                                                        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-violet-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-violet-500/25 rotate-3">
                                                            <Lock className="text-white w-8 h-8" />
                                                        </div>
                                                        <h3 className="text-xl font-bold text-slate-900 mb-2">
                                                            +{Math.max(0, totalCount - offers.filter(o => !o.isGhost).length)} offres masquées
                                                        </h3>
                                                        <p className="text-slate-500 text-sm mb-6 leading-relaxed">
                                                            Accédez à l'intégralité du marché caché et postulez avant tout le monde avec Trouve+.
                                                        </p>
                                                        <button
                                                            onClick={() => navigate('/trouve-plus')}
                                                            className="w-full py-3.5 bg-gradient-to-r from-blue-600 to-violet-600 rounded-xl text-white font-bold text-sm hover:shadow-lg hover:shadow-blue-500/25 transition-all transform hover:scale-[1.02] active:scale-[0.98]"
                                                        >
                                                            Débloquer avec Trouve+
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Bottom mask gradient for smooth cut-off */}
                                                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#F0F9FF] to-transparent z-10 pointer-events-none" />
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="p-10 text-center bg-white rounded-2xl border border-dashed border-gray-200 text-slate-400">
                                        Aucune offre exclusive pour le moment.
                                        <br /><span className="text-xs">Table: offres_vie_hors_bf_public</span>
                                    </div>
                                )}

                            </>
                        )}

                        {/* Pagination */}
                        {offers.length > 0 && totalCount > ITEMS_PER_PAGE && (
                            <div className="mt-6">
                                <ModernPagination
                                    currentPage={page}
                                    totalPages={Math.ceil(totalCount / ITEMS_PER_PAGE)}
                                    onPageChange={setPage}
                                />
                            </div>
                        )}
                    </div>

                    {/* Right Column: Detail View */}
                    <div className="hidden lg:block lg:col-span-8 sticky top-24">
                        {selectedOffer ? (
                            <OfferDetailView
                                offer={selectedOffer}
                                isFavorite={favorites.includes(selectedOffer.id)}
                                onToggleFavorite={toggleFavorite}
                            />
                        ) : (
                            <div className="h-[600px] bg-white rounded-3xl border border-slate-100 flex items-center justify-center text-slate-400 flex-col gap-4">
                                <Search size={48} className="text-slate-200" />
                                <p>Sélectionnez une offre pour voir les détails</p>
                            </div>
                        )}
                    </div>

                </div>
            </div>

            {/* Mobile Offer Drawer */}
            <MobileOfferDrawer
                isOpen={mobileDrawerOpen}
                onClose={() => setMobileDrawerOpen(false)}
                offer={selectedOffer}
                isFavorite={selectedOffer ? favorites.includes(selectedOffer.id) : false}
                onToggleFavorite={toggleFavorite}
            />
            {/* Premium Modal */}
            <PremiumModal
                isOpen={showPremiumModal}
                onClose={() => setShowPremiumModal(false)}
                title="Débloque le CV Matching"
                description="Active le CV Matching pour afficher en priorité les offres de VIE qui correspondent à ton profil, tes envies et ton CV."
                featureName="CV Matching"
            />
        </div >
    );
};

export default OffersPlusPage;
